import { PDFDocument, PDFImage, PDFPage } from "@cantoo/pdf-lib";

export type SignatureBoxCoordinateType = {
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
};

/**
 * Embeds a signature image onto a PDF at every specified signature box coordinate.
 *
 * Coordinates use a top-left origin in pixels — `page` is 1-based.
 * Internally converts to pdf-lib's bottom-left origin.
 *
 * @param pdfBytes - The original PDF as a Uint8Array or ArrayBuffer
 * @param signatureDataUri - base64 data URI for the signature image (PNG or JPEG)
 * @param coordinates - Array of signature box placements (page + x/y/width/height)
 * @returns Modified PDF as Uint8Array
 */
export const applySignaturesToPdf = async ({
  pdfBytes,
  signatureDataUri,
  coordinates,
}: {
  pdfBytes: Uint8Array | ArrayBuffer;
  signatureDataUri: string;
  coordinates: SignatureBoxCoordinateType[];
}): Promise<Uint8Array> => {
  if (coordinates.length === 0) {
    return pdfBytes instanceof Uint8Array ? pdfBytes : new Uint8Array(pdfBytes);
  }

  const pdfDoc = await PDFDocument.load(pdfBytes);
  const signatureImage = await embedImageFromDataUri(pdfDoc, signatureDataUri);
  drawImageInBoxes({
    pages: pdfDoc.getPages(),
    image: signatureImage,
    coordinates,
  });

  return pdfDoc.save();
};

export const embedImageFromDataUri = async (
  pdfDoc: PDFDocument,
  dataUri: string,
): Promise<PDFImage> => {
  const base64Data = dataUri.split(",")[1];
  if (!base64Data) {
    throw new Error("Invalid image data URI: missing base64 content");
  }

  const imageBytes = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));

  if (dataUri.startsWith("data:image/jpeg")) {
    return pdfDoc.embedJpg(imageBytes);
  }

  return pdfDoc.embedPng(imageBytes);
};

/**
 * Like {@link embedImageFromDataUri} but returns null instead of embedding an
 * image that would break the decoder. A PNG whose chunk lengths don't add up
 * (corruption, truncation, stray metadata chunks) makes pdf-lib's decoder read
 * past the buffer and hang the whole thread — which cannot be caught, so we
 * validate the chunk structure up front and skip malformed images.
 */
export const embedImageFromDataUriSafe = async (
  pdfDoc: PDFDocument,
  dataUri: string,
): Promise<PDFImage | null> => {
  const base64Data = dataUri.split(",")[1];
  if (!base64Data) {
    return null;
  }

  const imageBytes = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));

  if (dataUri.startsWith("data:image/jpeg")) {
    return pdfDoc.embedJpg(imageBytes);
  }

  if (!isStructurallyValidPng(imageBytes)) {
    return null;
  }

  return pdfDoc.embedPng(imageBytes);
};

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

/**
 * Verifies a PNG's chunks walk cleanly from IHDR to a terminal IEND without any
 * declared length overrunning the buffer. A drifting length (as produced by some
 * exporters' EXIF/metadata chunks, or by a base64 mangled in transit) is what
 * sends pdf-lib's decoder into an unbounded read; catching it here turns a hang
 * into a skipped stamp.
 */
const isStructurallyValidPng = (bytes: Uint8Array): boolean => {
  if (bytes.length < 8 + 12) return false;
  for (let i = 0; i < 8; i++) {
    if (bytes[i] !== PNG_SIGNATURE[i]) return false;
  }

  const readUint32 = (o: number) =>
    ((bytes[o]! << 24) |
      (bytes[o + 1]! << 16) |
      (bytes[o + 2]! << 8) |
      bytes[o + 3]!) >>>
    0;

  let offset = 8;
  let hasSeenIhdr = false;

  while (offset + 12 <= bytes.length) {
    const length = readUint32(offset);

    for (let i = 0; i < 4; i++) {
      const code = bytes[offset + 4 + i]!;
      const isAsciiLetter =
        (code >= 65 && code <= 90) || (code >= 97 && code <= 122);
      if (!isAsciiLetter) return false;
    }

    const type = String.fromCharCode(
      bytes[offset + 4]!,
      bytes[offset + 5]!,
      bytes[offset + 6]!,
      bytes[offset + 7]!,
    );

    if (offset === 8 && type !== "IHDR") return false;
    if (type === "IHDR") hasSeenIhdr = true;

    offset += 12 + length;

    if (offset > bytes.length) return false;

    if (type === "IEND") return hasSeenIhdr;
  }

  return false;
};

/**
 * Draws an already-embedded image into each box, scaled to fit while preserving
 * aspect ratio, left-aligned and vertically centered. Converts the top-left
 * origin coordinates to pdf-lib's bottom-left origin.
 */
export const drawImageInBoxes = ({
  pages,
  image,
  coordinates,
}: {
  pages: PDFPage[];
  image: PDFImage;
  coordinates: SignatureBoxCoordinateType[];
}): void => {
  for (const coord of coordinates) {
    const page = pages[coord.page - 1];

    if (!page) {
      continue;
    }

    const pageHeight = page.getHeight();
    const scaled = image.scaleToFit(coord.width, coord.height);
    const offsetY = (coord.height - scaled.height) / 2;
    const pdfY = pageHeight - coord.y - coord.height;

    page.drawImage(image, {
      x: coord.x,
      y: pdfY + offsetY,
      width: scaled.width,
      height: scaled.height,
    });
  }
};
